import React, { useEffect, useState } from "react";
import { pnpLogo } from "../../../Asserts";
import useDeviceType from "../../../Utiles/FindDevice";
import { useNavigate, useLocation } from "react-router";
import { useFormik } from "formik";
import { signUpValidationSchema } from "../../../Validation/formValidation";
import axios from "axios";
import { toast } from "react-toastify";
import { baseURL } from "../../../services/axiosConfig";
import { countries as countryData } from "countries-list";
import { NATIONALITY } from "../nationality";
import { FooterTab, MobileFooter } from "../../../../Components/GreenHome/Footer";
import InitialEntryDialog from "./InitialEntryDialog";
import FullRegistrationDialog from "./FullRegistrationDialog";

const countryOptions = Object.values(countryData).map((country) => ({
  label: country.name,
  value: country.name,
}));

const getPasswordError = (value = "") => {
  if (value === "") return "Password is required.";
  if (value.length < 8) return "At least 8 characters.";
  if (!/[A-Z]/.test(value)) return "Add an uppercase letter.";
  if (!/[a-z]/.test(value)) return "Add a lowercase letter.";
  if (!/[0-9]/.test(value)) return "Add a number.";
  if (!/[!@#$%^&*(),.?":{}|<>[\]\\/'`~_\-+=;]/.test(value)) return "Add a special character.";
  return "";
};

const FLOW_STAGE = { INITIAL: 1, FULL: 2, COMPLETE: 3 };

const Signup = () => {
  const deviceType = useDeviceType();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStage, setCurrentStage] = useState(FLOW_STAGE.INITIAL);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [canNavigateToLeague, setCanNavigateToLeague] = useState(false);
  const [optionalConsent, setOptionalConsent] = useState(false);
  const [whatsappConsent, setWhatsappConsent] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: "", lastName: "", displayName: "", email: "", phoneNumber: "",
      dateOfBirth: "", addressLine1: "", addressLine2: "", city: "", region: "",
      country: "", nationality: "", password: "", confirmPassword: "", agreeToTerms: false,
    },
    validationSchema: signUpValidationSchema,
    onSubmit: (values) => handleSignup(values),
    validateOnChange: false,
    validateOnBlur: true,
  });

  // 4. Prefill Location based on IP
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const res = await axios.get("https://ipapi.co/json/");
        if (res.data?.country_name) {
          formik.setFieldValue("country", res.data.country_name);
          formik.setFieldValue("nationality", res.data.country_name);
        }
      } catch (err) { console.error("Geo-fetch failed", err); }
    };
    fetchLocation();
  }, []);

  // 1 & 3. Step 1 Logic: Email Validation & Partial Data Storage
  const handleContinue = async () => {
  const errors = await formik.validateForm();
  const step1Fields = ['firstName', 'lastName', 'email'];
  let step1HasErrors = false;

  step1Fields.forEach((f) => {
    if (errors[f]) {
      formik.setFieldTouched(f, true);
      step1HasErrors = true;
    }
  });

  if (step1HasErrors) return;

  try {
    // 🔍 Check if email exists using token endpoint
    const payload = {
      email: formik.values.email.trim().toLowerCase(),
      password: "dummy",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    await axios.post(`${baseURL}/accounts/token/`, payload);

    // ❌ If token succeeds → user already exists
    formik.setFieldError("email", "This email is already registered.");
    toast.error("This email is already registered.");
    return;

  } catch (error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail?.toLowerCase?.() || "";
    const nonField =
      error?.response?.data?.non_field_errors?.[0]?.toLowerCase?.() || "";

    const isInvalidCreds =
      status === 400 &&
      (nonField.includes("unable to log in") ||
        nonField.includes("invalid credentials"));

    const isNoAccount =
      status === 404 || detail.includes("no active account found");

    // ❌ Exists in USERS table
    if (isInvalidCreds) {
      formik.setFieldError("email", "This email is already registered.");
      toast.error("This email is already registered.");
      return;
    }

    // ✅ NEW OR PARTIAL USER → CONTINUE REGISTRATION
    if (isNoAccount) {
      try {
        const res = await axios.post(`${baseURL}/accounts/user/signup/step1`, {
          first_name: formik.values.firstName,
          last_name: formik.values.lastName,
          email: formik.values.email.trim().toLowerCase(),
        });

        // 🔔 Analytics (unchanged)
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "registration_initiated",
            formId: "registerForm",
            firstName: formik.values.firstName,
            lastName: formik.values.lastName,
            emailAddress: formik.values.email,
            userId: res.data?.data?.id || "pending",
          });
        }

        // ✅ KEY FIX: MOVE TO NEXT STEP
        // toast.info("Welcome Back! please continue your registration.");
        setCurrentStage(FLOW_STAGE.FULL);
        return;

      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.detail ||
          "Welcome Back! Please continue your registration.";

        // ✅ EVEN IF PARTIAL USER ALREADY EXISTS → CONTINUE
        toast.info(msg);
        setCurrentStage(FLOW_STAGE.FULL);
        return;
      }
    }

    toast.error("You are already a registered player. Please proceed to login.");
  }
};


  const handleSignup = async (values) => {
    setIsSubmitting(true);
    setSubmitError("");
    const payload = {
      first_name: values.firstName.trim(),
      last_name: values.lastName.trim(),
      username: values.displayName.trim(),
      email: values.email.trim().toLowerCase(),
      mobile_number: values.phoneNumber.trim(),
      password: values.password,
      confirm_password: values.confirmPassword,
      date_of_birth: values.dateOfBirth,
      address_line_1: values.addressLine1.trim(),
      address_line_2: values.addressLine2.trim(),
      city: values.city.trim(),
      state: values.region.trim(),
      country: values.country,
      nationality: values.nationality,
      optional_consent: optionalConsent,
      whatsapp_consent: whatsappConsent,
    };

    try {
      const response = await axios.post(`${baseURL}/accounts/user/create`, payload);
      if (response?.data?.status === "Success") {
        const userId = response?.data?.data?.id;
        localStorage.setItem("user", JSON.stringify(userId));
        sessionStorage.setItem("pendingSignupEmail", values.email.trim().toLowerCase());
        sessionStorage.setItem("pendingSignupPassword", values.password);
        
        // GTM: Registration Completed
        if (window.dataLayer) {
          window.dataLayer.push({
            'event': 'registration_submitted',
            'formId': 'registerForm',
            'firstName': values.firstName,
            'lastName': values.lastName,
            'displayName': values.displayName,
            'emailAddress': values.email,
            'phoneNumber': values.phoneNumber,
            'dateOfBirth': values.dateOfBirth,
            'addressLine1': values.addressLine1,
            'addressLine2': values.addressLine2,
            'cityTown': values.city,
            'region': values.region,
            'country': values.country,
            'nationality': values.nationality,
            'marketingConsent': optionalConsent,
            'whatsappConsent': whatsappConsent,
            'userId': userId,
            'gender': 'Not Specified'
          });
        }
        setCanNavigateToLeague(true);
        setCurrentStage(FLOW_STAGE.COMPLETE);
        navigate(`/league/${values.email.toLowerCase()}`);
      }
    } catch (error) {
      setIsSubmitting(false);
      const data = error?.response?.data || {};
      const status = error?.response?.status;

      const message =
        data?.message ||
        data?.detail ||
        data?.error ||
        (Array.isArray(data) ? data[0] : undefined) ||
        (typeof data === "string" ? data : undefined);

      const fieldErrors = typeof data === "object" ? data : {};
      if (fieldErrors?.email) {
        formik.setFieldError("email", Array.isArray(fieldErrors.email) ? fieldErrors.email[0] : fieldErrors.email);
      }
      if (fieldErrors?.username) {
        formik.setFieldError("displayName", Array.isArray(fieldErrors.username) ? fieldErrors.username[0] : fieldErrors.username);
      }
      if (fieldErrors?.mobile_number) {
        formik.setFieldError("phoneNumber", Array.isArray(fieldErrors.mobile_number) ? fieldErrors.mobile_number[0] : fieldErrors.mobile_number);
      }
      if (fieldErrors?.password) {
        formik.setFieldError("password", Array.isArray(fieldErrors.password) ? fieldErrors.password[0] : fieldErrors.password);
      }
      if (fieldErrors?.date_of_birth) {
        formik.setFieldError("dateOfBirth", Array.isArray(fieldErrors.date_of_birth) ? fieldErrors.date_of_birth[0] : fieldErrors.date_of_birth);
      }

      if (status === 409) {
        toast.error("Email or display name already exists.");
        return;
      }

      toast.error(message || "Display name already exists.");
    }
  };

  // 6. GTM: Registration Start
  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({ 'event': 'registration_start', 'formId': 'registerForm' });
    }
  }, []);

  return (
    <div style={{ backgroundImage: `url('/v2/images/bg/${deviceType === "mobile" ? "auth-bg-mobile.png" : "auth-bg.png"}')` }}
      className="bg-cover bg-no-repeat bg-center min-h-screen flex flex-col justify-between">
      
      {/* 5. Logo Left */}
      <div className="w-full flex justify-start pt-8 pl-8">
        <img src={pnpLogo} alt="Logo" className="h-20 cursor-pointer" onClick={() => navigate("/")} />
      </div>

      <div className={`flex justify-center w-full my-auto px-4 ${currentStage !== FLOW_STAGE.COMPLETE ? 'block' : 'hidden'}`}>
        {currentStage === FLOW_STAGE.INITIAL ? (
          <InitialEntryDialog formik={formik} handleContinue={handleContinue} handleFieldChange={formik.handleChange} />
        ) : (
          <FullRegistrationDialog
            formik={formik} handleFieldChange={formik.handleChange} getPasswordError={getPasswordError}
            isSubmitting={isSubmitting} optionalConsent={optionalConsent} setOptionalConsent={setOptionalConsent}
            whatsappConsent={whatsappConsent} setWhatsappConsent={setWhatsappConsent}
            countryOptions={countryOptions} NATIONALITY={NATIONALITY} setCurrentStage={setCurrentStage} FLOW_STAGE={FLOW_STAGE}
          />
        )}
      </div>

      {deviceType === "mobile" ? <MobileFooter /> : <FooterTab component="privacyPolicy" className="mt-auto" />}
    </div>
  );
};

export default Signup;
